import React, { useEffect } from "react";
import { CopyButton, RecordTypePill } from "./Ui";
import { recordTypeMeta } from "../utils";

export default function HelpDrawer({ open, onClose, meta }) {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;
  const guidance = (meta && meta.guidance) || { howTo: [], noteExamples: [] };
  const address = meta ? meta.inboundAddress : "";
  const routable = (meta && (meta.routableRecordTypes || (meta.picklists && meta.picklists.recordTypes && meta.picklists.recordTypes.map((r) => r.name)))) || [];
  const guide = (meta && meta.recordTypeGuide) || {};

  return (
    <div className="ob-drawer-backdrop" onMouseDown={onClose}>
      <aside className="ob-drawer" onMouseDown={(e) => e.stopPropagation()} role="dialog" aria-label="How to use Opp Bot">
        <header className="ob-drawer__head">
          <div>
            <div className="ob-eyebrow">How it works</div>
            <h2>Forward an email, get a Salesforce opportunity</h2>
          </div>
          <button type="button" className="ob-iconbtn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </header>

        <div className="ob-drawer__body">
          <div className="ob-address-card">
            <div>
              <div className="ob-eyebrow">Forward to</div>
              <div className="ob-address">{address || "not configured"}</div>
            </div>
            {address && <CopyButton text={address} label="Copy address" />}
          </div>

          <ol className="ob-steps">
            {guidance.howTo.map((step, i) => (
              <li key={i}>{step}</li>
            ))}
          </ol>

          <h3>Where it routes</h3>
          <p className="ob-muted">Each email becomes one of these opportunity types, decided from your notes first, then the thread and who is involved.</p>
          <ul className="ob-routes">
            {routable.map((name) => {
              const m = recordTypeMeta(name);
              const g = guide[name] || {};
              return (
                <li key={name}>
                  <RecordTypePill name={name} size="sm" />
                  <div>
                    <div className="ob-routes__when">{g.summary || (g.when ? g.when.replace(/^the /, "The ").split(". Signals")[0] : m.blurb)}</div>
                    {g.naming && <div className="ob-routes__naming">Named {g.naming}</div>}
                  </div>
                </li>
              );
            })}
          </ul>

          <h3>Notes you can put at the top</h3>
          <p className="ob-muted">Plain English is fine. These are examples of what the bot understands.</p>
          <table className="ob-table ob-table--compact">
            <thead>
              <tr>
                <th>You write</th>
                <th>Bot does</th>
              </tr>
            </thead>
            <tbody>
              {guidance.noteExamples.map((n) => (
                <tr key={n.example}>
                  <td>
                    <code>{n.example}</code>
                  </td>
                  <td>{n.meaning}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <h3>Closing a deal</h3>
          <p className="ob-muted">Forward the decisive email - a "we're going with someone else", a signed agreement, a confirmed budget - optionally with a one-line note such as <code>Move to Closed Lost</code>.</p>
          <ul className="ob-bullets">
            <li>The bot picks the matching opportunity on the prospect's account and moves it to <b>Closed Lost</b> or <b>Closed Won</b>, setting the close date to the day the decision was made.</li>
            <li>Lost: the reason and the quote it relied on are written to the opportunity's Loss Reason field, the way the team already does by hand.</li>
            <li>Won: the amount is required, and Curate wins also need the number of events. Say them in your notes if the thread doesn't (<code>Closed Won - $35k, 2 dinners</code>).</li>
            <li>
              If the opportunity is already closed nothing changes - the email is just logged on it. Close-outs {meta && meta.autoClose ? "are applied automatically when confident" : "always wait here for approval"}; Closed Won always waits for approval.
            </li>
          </ul>

          {guidance.directRequest && (
            <>
              <h3>Ask the bot directly</h3>
              <p className="ob-muted">{guidance.directRequest.intro}</p>
              <ul className="ob-bullets">
                {(guidance.directRequest.fields || []).map((f, i) => (
                  <li key={i}>{f}</li>
                ))}
              </ul>
              {guidance.directRequest.example && (
                <div className="ob-example">
                  <div className="ob-eyebrow">Example</div>
                  <pre>{guidance.directRequest.example}</pre>
                  <CopyButton text={guidance.directRequest.example} label="Copy example" />
                </div>
              )}
              {meta && meta.linkedInEnabled === false && <p className="ob-muted">LinkedIn lookups are currently switched off, so include the person's email and company in the message.</p>}
            </>
          )}

          <h3>What happens in Salesforce</h3>
          <ul className="ob-bullets">
            <li>Finds the prospect company and people by email domain (or from their LinkedIn profile); creates the Account / Contacts only if they do not exist.</li>
            <li>Creates the Opportunity with the record type above (stage, lead source and Curate category chosen from that record type's own picklists), updates the matching open one on the same account and business line, or closes it.</li>
            <li>Adds the people as Opportunity Contact Roles and logs the forwarded email as a completed Task on the Opportunity.</li>
            <li>
              Writes automatically only when confidence is at least {meta ? Math.round(meta.autoApplyMinConfidence * 100) : 80}% and nothing is ambiguous. Otherwise it waits here for approval.
              {meta && meta.dryRun ? " Dry-run mode is on, so everything currently waits for approval." : ""}
            </li>
            <li>Only emails from {meta && meta.allowedSenderDomains ? meta.allowedSenderDomains.map((d) => `@${d}`).join(", ") : "company"} addresses are accepted.</li>
          </ul>
        </div>
      </aside>
    </div>
  );
}
